import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, SettingsIcon, ChevronDownIcon, ChevronUpIcon, ChevronRightIcon, InfoIcon, CreditCardIcon, WalletIcon, CreditCardIcon as CardIcon, ExternalLinkIcon } from 'lucide-react';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
export type PaymentProvider = {
  id: string;
  name: string;
  icon: React.ReactNode;
};
interface CommonPaymentOptionsProps {
  metadata: MetadataItem[];
  redirectUrl: string;
  expiryDate: string;
  onMetadataChange: (metadata: MetadataItem[]) => void;
  onRedirectUrlChange: (url: string) => void;
  onExpiryDateChange: (date: string) => void;
  hideExpiry?: boolean;
  hideRedirect?: boolean;
  selectedPaymentProviders?: string[];
  onSelectedPaymentProvidersChange?: (providers: string[]) => void;
  storePaymentMethod?: boolean;
  onStorePaymentMethodChange?: (value: boolean) => void;
  passFeesToCustomer?: boolean;
  onPassFeesToCustomerChange?: (value: boolean) => void;
}
export function CommonPaymentOptions({
  metadata,
  redirectUrl,
  expiryDate,
  onMetadataChange,
  onRedirectUrlChange,
  onExpiryDateChange,
  hideExpiry = false,
  hideRedirect = false,
  selectedPaymentProviders = [],
  onSelectedPaymentProvidersChange,
  storePaymentMethod,
  onStorePaymentMethodChange,
  passFeesToCustomer: externalPassFees,
  onPassFeesToCustomerChange
}: CommonPaymentOptionsProps) {
  const {
    t
  } = useTranslation('payments');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [optionsSectionExpanded, setOptionsSectionExpanded] = useState(false);
  const [expiryDays, setExpiryDays] = useState(30);
  const [showPaymentProviders, setShowPaymentProviders] = useState(false);
  const [internalPassFees, setInternalPassFees] = useState(false);
  const passFeesToCustomer = externalPassFees !== undefined ? externalPassFees : internalPassFees;
  const handlePassFeesChange = (checked: boolean) => {
    if (onPassFeesToCustomerChange) {
      onPassFeesToCustomerChange(checked);
    } else {
      setInternalPassFees(checked);
    }
  };
  const paymentProviders: PaymentProvider[] = [{
    id: 'card-payments',
    name: 'Card Payments',
    icon: <CreditCardIcon size={15} className="text-[#E2231A]" data-id="element-7059" />
  }, {
    id: 'alipay',
    name: 'Alipay',
    icon: <WalletIcon size={15} className="text-[#E2231A]" data-id="element-7060" />
  }, {
    id: 'wechatpay',
    name: 'WechatPay',
    icon: <WalletIcon size={15} className="text-green-500" data-id="element-7061" />
  }, {
    id: 'pomelo-card',
    name: 'Bank of Maldives Card',
    icon: <CardIcon size={15} className="text-purple-500" data-id="element-7062" />
  }, {
    id: 'linepay',
    name: 'LinePay',
    icon: <WalletIcon size={15} className="text-green-600" data-id="element-7063" />
  }];
  const defaultExpiryDays = 30;
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  useEffect(() => {
    if (expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(p => parseInt(p, 10));
      const expiryDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setExpiryDays(Math.ceil((expiryDateObj.getTime() - today.getTime()) / (1000 * 3600 * 24)));
    }
  }, [expiryDate]);
  useEffect(() => {
    if (!expiryDate) {
      const date = new Date();
      date.setDate(date.getDate() + defaultExpiryDays);
      onExpiryDateChange(formatDate(date));
    }
  }, [expiryDate, onExpiryDateChange, defaultExpiryDays]);
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  };
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(p => parseInt(p, 10));
    return new Date(year, month - 1, day);
  };
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = showDatePicker ? expiryDate ? parseDate(expiryDate) : today : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const weeks: (Date | null)[][] = [];
    let days: (Date | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) days.push(null);
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    calendar.month.setMonth(calendar.month.getMonth() - 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    calendar.month.setMonth(calendar.month.getMonth() + 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    onExpiryDateChange(formatDate(date));
    setShowDatePicker(false);
  };
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const togglePaymentProvider = (providerId: string) => {
    if (!onSelectedPaymentProvidersChange) return;
    const updated = selectedPaymentProviders.includes(providerId) ? selectedPaymentProviders.filter(id => id !== providerId) : [...selectedPaymentProviders, providerId];
    onSelectedPaymentProvidersChange(updated);
  };
  const hasMetadata = metadata && metadata.length > 0;
  const hasRedirectUrl = redirectUrl && redirectUrl.length > 0;
  const hasExpiryDate = expiryDate && expiryDate.length > 0;
  const hasSelectedProviders = selectedPaymentProviders && selectedPaymentProviders.length > 0;
  const hasStorePayment = storePaymentMethod === true;
  const hasPassFees = passFeesToCustomer === true;
  const hasAnyData = hasMetadata || hasRedirectUrl || hasExpiryDate || hasSelectedProviders || hasStorePayment || hasPassFees;
  const quickDates = [{
    label: '7 days',
    days: 7
  }, {
    label: '30 days',
    days: 30
  }, {
    label: '90 days',
    days: 90
  }];
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7064">
      <button className="w-full text-left flex items-center justify-between px-6 py-4" onClick={() => setOptionsSectionExpanded(!optionsSectionExpanded)} data-id="element-7065">
        <div className="flex items-center gap-2" data-id="element-7066">
          <SettingsIcon size={16} className="text-[#9F140F]" data-id="element-7067" />
          <span className="text-sm font-semibold text-gray-900" data-id="element-7068">
            {t('components.CommonPaymentOptions.additional_options')}
          </span>
          {!optionsSectionExpanded && hasAnyData && <span className="text-xs text-gray-500 font-normal ml-1" data-id="element-7069">
              —{' '}
              {[hasExpiryDate && !hideExpiry ? `Expires ${expiryDate}` : null, hasStorePayment ? 'Store card enabled' : null, hasPassFees ? 'Fees passed to customer' : null, hasSelectedProviders ? `${selectedPaymentProviders.length} methods` : null, hasRedirectUrl && !hideRedirect ? 'Redirect URL' : null, hasMetadata ? `${metadata.length} metadata` : null].filter(Boolean).join(', ')}
            </span>}
        </div>
        {optionsSectionExpanded ? <ChevronDownIcon size={16} className="text-gray-400" data-id="element-7070" /> : <ChevronRightIcon size={16} className="text-gray-400" data-id="element-7071" />}
      </button>

      {optionsSectionExpanded && <div data-id="element-7072">
          {/* ── Store Payment Method ── */}
          {onStorePaymentMethodChange !== undefined && <div className="border-t border-gray-200 px-6 py-5" data-id="element-7073">
              <div className="flex items-start justify-between" data-id="element-7074">
                <div data-id="element-7075">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-7076">
                    {t('components.CommonPaymentOptions.store_payment_method')}
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-7077">
                    {t('components.CommonPaymentOptions.customer_authorizes_saving_their_payment_method')}{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-7078">
                      {t('components.CommonPaymentOptions.cards')}
                    </span>{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-7079">
                      {t('components.CommonPaymentOptions.alipay')}
                    </span>{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-7080">
                      {t('components.CommonPaymentOptions.wechatpay')}
                    </span>
                  </p>
                </div>
                <label className="flex items-center cursor-pointer flex-shrink-0 mt-0.5 ml-4" data-id="element-7081">
                  <input type="checkbox" className="sr-only" checked={storePaymentMethod || false} onChange={e => onStorePaymentMethodChange(e.target.checked)} data-id="element-7082" />

                  <div className={`w-10 h-[22px] rounded-full transition-colors ${storePaymentMethod ? 'bg-[#E2231A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-7083">
                    <div className={`w-[18px] h-[18px] rounded-full bg-white transform transition-transform shadow-sm ${storePaymentMethod ? 'translate-x-[18px]' : ''}`} data-id="element-7084" />
                  </div>
                </label>
              </div>
            </div>}

          {/* ── Pass Fees to Customer ── */}
          <div className="border-t border-gray-200 px-6 py-5" data-id="element-7085">
            <div className="flex items-start justify-between" data-id="element-7086">
              <div data-id="element-7087">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-7088">
                  {t('components.CommonPaymentOptions.pass_fees_to_customer')}
                </h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-7089">
                  {t('components.CommonPaymentOptions.transaction_processing_fees_are_added_on')}
                </p>
              </div>
              <label className="flex items-center cursor-pointer flex-shrink-0 mt-0.5 ml-4" data-id="element-7090">
                <input type="checkbox" className="sr-only" checked={passFeesToCustomer} onChange={e => handlePassFeesChange(e.target.checked)} data-id="element-7091" />

                <div className={`w-10 h-[22px] rounded-full transition-colors ${passFeesToCustomer ? 'bg-[#E2231A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-7092">
                  <div className={`w-[18px] h-[18px] rounded-full bg-white transform transition-transform shadow-sm ${passFeesToCustomer ? 'translate-x-[18px]' : ''}`} data-id="element-7093" />
                </div>
              </label>
            </div>
          </div>

          {/* ── Link Expiration ── */}
          {!hideExpiry && <div className="border-t border-gray-200 px-6 py-5" data-id="element-7094">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-7095">
                {t('components.CommonPaymentOptions.link_expiration')}
              </h4>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-7096">
                {t('components.CommonPaymentOptions.expires_in')} {expiryDays}{' '}
                {t('components.CommonPaymentOptions.days_default')}{' '}
                {defaultExpiryDays}{' '}
                {t('components.CommonPaymentOptions.days_change_in')}{' '}
                <span className="text-[#9F140F] cursor-pointer" data-id="element-7097">
                  {t('components.CommonPaymentOptions.payment_settings')}
                </span>
                .
              </p>
              <div className="relative mt-3" data-id="element-7098">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7099">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-7100" />
                </div>
                <input type="text" className={`${inputClass} pl-9 cursor-pointer`} placeholder={t('components.CommonPaymentOptions.attrs.mmddyyyy')} value={expiryDate} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-7101" />

                <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-7102">
                  {showDatePicker ? <ChevronUpIcon size={14} className="text-gray-400" data-id="element-7103" /> : <ChevronDownIcon size={14} className="text-gray-400" data-id="element-7104" />}
                </div>

                {/* Date Picker — fixed width */}
                {showDatePicker && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80 left-0" data-id="element-7105">
                    <div className="flex justify-between items-center mb-3" data-id="element-7106">
                      <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-7107">
                        <ChevronDownIcon size={14} className="transform rotate-90" data-id="element-7108" />
                      </button>
                      <div className="text-sm font-semibold text-gray-900" data-id="element-7109">
                        {calendar.month.toLocaleString('default', {
                  month: 'long',
                  year: 'numeric'
                })}
                      </div>
                      <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-7110">
                        <ChevronDownIcon size={14} className="transform -rotate-90" data-id="element-7111" />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 mb-1" data-id="element-7112">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} data-id="element-7113">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center" data-id="element-7114">
                      {calendar.weeks.flatMap(week => week.map((date, i) => date ? <button key={i} type="button" disabled={isPast(date)} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-colors ${isSelected(date) ? 'bg-[#E2231A] text-white' : isToday(date) ? 'border border-[#E2231A] text-[#E2231A]' : isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => handleDateSelect(date)} data-id="element-7115">
                              {date.getDate()}
                            </button> : <div key={i} className="w-8 h-8" data-id="element-7116" />))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2" data-id="element-7117">
                      {quickDates.map(q => <button key={q.days} type="button" className="flex-1 text-xs font-medium text-[#9F140F] hover:bg-[#E2231A]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() + q.days);
                onExpiryDateChange(formatDate(d));
                setShowDatePicker(false);
              }} data-id="element-7118">
                          {q.label}
                        </button>)}
                    </div>
                  </div>}
              </div>
            </div>}

          {/* ── Payment Methods ── */}
          {onSelectedPaymentProvidersChange && <div className="border-t border-gray-200 px-6 py-5" data-id="element-7119">
              <div className="flex items-start justify-between" data-id="element-7120">
                <div data-id="element-7121">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-7122">
                    {t('components.CommonPaymentOptions.limit_payment_methods')}
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-7123">
                    {t('components.CommonPaymentOptions.restrict_which_payment_methods_are_available')}
                  </p>
                </div>
                {selectedPaymentProviders.length > 0 && <button type="button" className="text-xs text-[#9F140F] hover:text-[#9F140F] font-medium flex-shrink-0 ml-4 mt-0.5" onClick={() => onSelectedPaymentProvidersChange([])} data-id="element-7124">
                    {t('components.CommonPaymentOptions.clear')}
                  </button>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3" data-id="element-7125">
                {paymentProviders.map(provider => <label key={provider.id} className={`flex items-center gap-2.5 p-2.5 border rounded-lg cursor-pointer transition-colors text-sm ${selectedPaymentProviders.includes(provider.id) ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-7126">
                    <input type="checkbox" className="h-3.5 w-3.5 text-[#E2231A] focus:ring-[#E2231A] border-gray-300 rounded" checked={selectedPaymentProviders.includes(provider.id)} onChange={() => togglePaymentProvider(provider.id)} data-id="element-7127" />

                    <span className="flex-shrink-0" data-id="element-7128">{provider.icon}</span>
                    <span className="text-xs font-medium text-gray-700 truncate" data-id="element-7129">
                      {provider.name}
                    </span>
                  </label>)}
              </div>
              {selectedPaymentProviders.length > 0 && <p className="mt-2 text-[11px] text-gray-500 flex items-center" data-id="element-7130">
                  <InfoIcon size={11} className="mr-1 text-[#9F140F] flex-shrink-0" data-id="element-7131" />

                  {t('components.CommonPaymentOptions.only_selected_methods_will_be_shown')}
                </p>}
            </div>}

          {/* ── Redirect URL ── */}
          {!hideRedirect && <div className="border-t border-gray-200 px-6 py-5" data-id="element-7132">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-7133">
                {t('components.CommonPaymentOptions.success_redirect_url')}
              </h4>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-7134">
                {t('components.CommonPaymentOptions.where_to_redirect_customers_after_a')}
              </p>
              <div className="relative mt-3" data-id="element-7135">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7136">
                  <ExternalLinkIcon size={14} className="text-gray-400" data-id="element-7137" />
                </div>
                <input type="url" className={`${inputClass} pl-9`} placeholder="https://example.com/thank-you" value={redirectUrl} onChange={e => onRedirectUrlChange(e.target.value)} data-id="element-7138" />
              </div>
            </div>}

          {/* ── Metadata ── */}
          <div className="border-t border-gray-200 px-6 py-5" data-id="element-7139">
            <MetadataEditor metadata={metadata} onChange={onMetadataChange} data-id="element-7140" />
          </div>
        </div>}
    </div>;
}